import { computed, Injectable, OnDestroy, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { IWFProcessConfig } from "rl-common/services/entity-config/entity-config.models";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { TokenStorageService } from "rl-common/services/token-storage/token-storage.service";
import { BehaviorSubject, ReplaySubject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, tap } from "rxjs/operators";

export interface IWorkflowRouteParams {
	charTypeId: number;
	processId: number;
}

@Injectable()
export class WorkflowProcessesService implements OnDestroy {
	private readonly _entityConfigService = inject(EntityConfigService);
	private readonly _oneConfigService = inject(OneConfigService);
	private readonly _tokenStorageService = inject(TokenStorageService);
	private _route = inject(ActivatedRoute);


	wfRouteParams = toSignal(
		this._route.params.pipe(
			map(params => {
				// grab the params from the route
				const charTypeIdParam = params?.["charTypeId"];
				const processIdParam = params?.["processId"];
				if (charTypeIdParam === null || processIdParam === null) {
					return null;
				}

				// parse the number
				const charTypeId = +charTypeIdParam;
				const processId = +processIdParam;

				if (!isFinite(charTypeId) || !isFinite(processId)) {
					// TODO: consider redirecting here
					return null;
				}

				const wfRouteParams: IWorkflowRouteParams = {
					charTypeId: charTypeId,
					processId: processId
				};

				return wfRouteParams;
			}),
			filter(wfParams => !!wfParams),
			distinctUntilChanged()
		)
	);

	charTypeId = computed(() => {
		return this.wfRouteParams()?.charTypeId;
	});

	processId = computed(() => {
		return this.wfRouteParams()?.processId;
	});

	wfProcessOnComplete$ = new BehaviorSubject<boolean>(false);
	wfStatusOnComplete$ = new BehaviorSubject<boolean>(false);
	wfActionOnComplete$ = new BehaviorSubject<boolean>(false);
	wfNotifOnComplete$ = new BehaviorSubject<boolean>(false);
	workflowProcessesData$ = new ReplaySubject<IWFProcessConfig[]>(1);
	wfDocTemplateOnComplete$ = new BehaviorSubject<boolean>(false);

	private readonly _subs: Subscription[] = [];

	constructor() {
		const sub = this._entityConfigService.getWFProcesses().pipe(
			tap(results => {
				this.workflowProcessesData$.next(results);
			})
		).subscribe();

		this._subs.push(sub);
	}

	refreshOneConfig() {
		const sub = this._oneConfigService.refresh(this._tokenStorageService.jwt).subscribe();
		this._subs.push(sub);
	}

	ngOnDestroy() {
		this._subs.forEach(s => s.unsubscribe());
	}
}
